import React, { useCallback, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { FiUpload, FiCheck } from 'react-icons/fi';
import { uploadImage } from '../../../shared/services/api/implementation/impl';
import { addCompanyDocument } from '../../services/api/implementation/impl';
import { DOCUMENT_CATALOGUE, formationProofTypesFor } from '../../constants/kyb';

/**
 * "Prepare Company Details" document picker.
 *
 * Recommended documents are the ones that satisfy the formation-proof requirement for the
 * selected entity type; everything else is optional supporting evidence. The card layout
 * mirrors the onboarding mockup.
 *
 * NOTE ON THE MOCKUP: the design shows a "Setup Forecast — % auto-fill ready" panel and
 * star ratings for how much each document will populate. Those describe AI extraction,
 * which is phase 1 and has no backend yet. Rather than render a fake progress figure,
 * this shows what each document actually does today: satisfy a policy requirement. The
 * forecast panel should be added when the extraction endpoints exist and can produce a
 * real number.
 */
const CompanyDocumentPicker = ({ companyId, entityType, uploadedTypes = [], onUploaded }) => {
  const [busyType, setBusyType] = useState(null);

  const acceptedProofs = useMemo(() => formationProofTypesFor(entityType), [entityType]);

  const { recommended, optional } = useMemo(() => {
    const isProof = (doc) => acceptedProofs.includes(doc.value);
    return {
      recommended: DOCUMENT_CATALOGUE.filter(isProof),
      optional: DOCUMENT_CATALOGUE.filter((doc) => !isProof(doc))
    };
  }, [acceptedProofs]);

  const handleFile = useCallback(
    async (docType, file) => {
      if (!file) return;
      setBusyType(docType);
      try {
        const formData = new FormData();
        formData.append('files', file);
        const result = await uploadImage(formData);
        const uploaded = result?.data?.files?.[0];
        if (!uploaded?.presignedUrl) throw new Error('Upload did not return a file URL');

        await addCompanyDocument(companyId, {
          doc_type: docType,
          file_url: uploaded.presignedUrl,
          original_filename: uploaded.originalName || file.name
        });

        toast.success('Document uploaded');
        if (onUploaded) await onUploaded();
      } catch (err) {
        toast.error(
          err?.response?.data?.message || err?.message || 'Could not upload the document'
        );
      } finally {
        setBusyType(null);
      }
    },
    [companyId, onUploaded]
  );

  const renderCard = (doc) => {
    const isUploaded = uploadedTypes.includes(doc.value);
    const isBusy = busyType === doc.value;

    return (
      <div
        key={doc.value}
        className={`flex items-center justify-between gap-3 rounded-lg border p-3 ${
          isUploaded ? 'border-emerald-300 bg-emerald-50' : 'border-stone-200 bg-white'
        }`}>
        <div className="flex items-start gap-3">
          <span
            className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded ${
              isUploaded ? 'bg-emerald-100 text-emerald-700' : 'bg-stone-100 text-stone-500'
            }`}>
            {isUploaded ? <FiCheck /> : <FiUpload />}
          </span>
          <div>
            <p className="text-sm text-stone-800">{doc.label}</p>
            <p className="text-xs text-stone-500">{doc.description}</p>
          </div>
        </div>

        <label
          htmlFor={`kyb-doc-${doc.value}`}
          className={`relative shrink-0 cursor-pointer rounded border px-3 py-1.5 text-xs ${
            isBusy
              ? 'border-stone-200 bg-stone-100 text-stone-400'
              : 'border-stone-300 bg-white text-stone-700 hover:bg-stone-50'
          }`}>
          <input
            id={`kyb-doc-${doc.value}`}
            type="file"
            className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
            accept=".pdf,.png,.jpg,.jpeg"
            disabled={isBusy}
            onChange={(event) => handleFile(doc.value, event.target.files?.[0])}
          />
          {isBusy ? 'Uploading…' : isUploaded ? 'Replace' : 'Upload'}
        </label>
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <div>
        <p className="text-sm text-stone-800">Prepare company details</p>
        <p className="text-xs text-stone-500">
          {entityType
            ? `Upload one of the accepted formation proofs for ${entityType}.`
            : 'Select a legal entity type to see which formation proof is accepted.'}
        </p>
      </div>

      {recommended.length > 0 && (
        <div className="flex flex-col gap-2">
          <p className="text-xs uppercase tracking-wide text-stone-400">
            Required — one of these
          </p>
          {recommended.map(renderCard)}
        </div>
      )}

      <div className="flex flex-col gap-2">
        <p className="text-xs uppercase tracking-wide text-stone-400">Optional</p>
        {optional.map(renderCard)}
      </div>
    </div>
  );
};

export default CompanyDocumentPicker;
